// client/src/pages/admin/NetworkPolicies.tsx
import React, { useEffect, useRef, useState } from 'react';
import { ChevronRight, Plus, Save, Shield, Trash2, X, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNetworkPolicy } from '../../contexts/NetworkPolicyContext';

const ACCENT = '#7aa2ff';

// ─── Types ────────────────────────────────────────────────────────────────────
type NetworkPolicy = {
  id:          string;
  name:        string;
  description: string;
  enabled:     boolean;
  cidrs:       string[];
  createdAt:   string;
  createdBy:   string;
  updatedAt?:  string;
};

type NetworkConfig = {
  enabled:      boolean;
  exemptAdmins: boolean;
  policies:     NetworkPolicy[];
};

// ─── API helpers ──────────────────────────────────────────────────────────────
async function apiFetch(path: string, opts?: RequestInit) {
  const r    = await fetch(path, { credentials: 'include', ...opts });
  if (r.status === 204) return null;
  const data = await r.json();
  if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
  return data;
}

// ─── CIDR validation (client-side) ───────────────────────────────────────────
function isValidIPv4(ip: string): boolean {
  return /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(ip) &&
    ip.split('.').every(p => { const n = parseInt(p, 10); return n >= 0 && n <= 255; });
}
function isValidIPv6(ip: string): boolean {
  return ip.includes(':') && /^[0-9a-fA-F:]+$/.test(ip);
}
function isValidCidr(entry: string): boolean {
  const slash = entry.indexOf('/');
  if (slash < 0) return isValidIPv4(entry) || isValidIPv6(entry);
  const ip     = entry.slice(0, slash);
  const prefix = parseInt(entry.slice(slash + 1), 10);
  if (isNaN(prefix)) return false;
  if (isValidIPv4(ip)) return prefix >= 0 && prefix <= 32;
  if (isValidIPv6(ip)) return prefix >= 0 && prefix <= 128;
  return false;
}

// ─── Toggle switch ────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, label, sublabel }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  sublabel?: string;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
      <div
        onClick={() => onChange(!checked)}
        style={{
          position:        'relative',
          width:           40,
          height:          22,
          borderRadius:    11,
          background:      checked ? ACCENT : 'rgba(255,255,255,.15)',
          transition:      'background .2s',
          flexShrink:      0,
          cursor:          'pointer',
        }}
      >
        <div style={{
          position:   'absolute',
          top:        3,
          left:       checked ? 21 : 3,
          width:      16,
          height:     16,
          borderRadius: '50%',
          background: '#fff',
          transition: 'left .2s',
          boxShadow:  '0 1px 3px rgba(0,0,0,.3)',
        }} />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
        {sublabel && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{sublabel}</div>}
      </div>
    </label>
  );
}

// ─── CIDR editor ─────────────────────────────────────────────────────────────
function CidrEditor({ cidrs, onChange }: { cidrs: string[]; onChange: (c: string[]) => void }) {
  const [input, setInput]       = useState('');
  const [bulkMode, setBulkMode] = useState(false);
  const [bulk, setBulk]         = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  function addSingle() {
    const entry = input.trim();
    if (!entry) return;
    if (!isValidCidr(entry)) { toast.error(`Invalid CIDR: ${entry}`); return; }
    if (cidrs.includes(entry)) { setInput(''); return; }
    onChange([...cidrs, entry]);
    setInput('');
  }

  function applyBulk() {
    const lines = bulk.split(/[\n,\s]+/).map(l => l.trim()).filter(Boolean);
    const bad   = lines.filter(l => !isValidCidr(l));
    if (bad.length > 0) { toast.error(`Invalid entries: ${bad.slice(0, 5).join(', ')}${bad.length > 5 ? '…' : ''}`); return; }
    const merged = Array.from(new Set([...cidrs, ...lines]));
    onChange(merged);
    setBulkMode(false);
    setBulk('');
    toast.success(`Added ${lines.length} CIDR(s)`);
  }

  const badCount = cidrs.filter(c => !isValidCidr(c)).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, color: 'var(--muted)', flex: 1 }}>
          {cidrs.length} entr{cidrs.length === 1 ? 'y' : 'ies'}
          {badCount > 0 && <span style={{ color: '#ff6b6b', marginLeft: 6 }}>({badCount} invalid)</span>}
        </span>
        <button
          className="btn"
          type="button"
          onClick={() => { setBulkMode(b => !b); setBulk(bulkMode ? '' : cidrs.join('\n')); }}
          style={{ fontSize: 11, padding: '3px 8px' }}
        >
          {bulkMode ? 'Back to list' : 'Bulk edit'}
        </button>
      </div>

      {bulkMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea
            className="input"
            value={bulk}
            onChange={e => setBulk(e.target.value)}
            placeholder="One IP/CIDR per line, or comma-separated"
            rows={12}
            style={{ fontSize: 12, fontFamily: 'monospace', resize: 'vertical', width: '100%' }}
          />
          <button className="btn primary" type="button" onClick={applyBulk} style={{ fontSize: 12, gap: 5 }}>
            <Save size={12} /> Apply
          </button>
        </div>
      ) : (
        <>
          {/* Add single */}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              ref={inputRef}
              className="input"
              type="text"
              placeholder="e.g. 192.168.1.0/24 or 2001:db8::/32"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSingle()}
              style={{ flex: 1, fontSize: 12, fontFamily: 'monospace' }}
            />
            <button className="btn" type="button" onClick={addSingle} style={{ fontSize: 11, gap: 4 }}>
              <Plus size={12} /> Add
            </button>
          </div>

          {/* List */}
          {cidrs.length === 0 ? (
            <div className="muted" style={{ fontSize: 12 }}>No CIDRs — add entries above or use Bulk edit.</div>
          ) : (
            <div style={{
              maxHeight:    240,
              overflowY:    'auto',
              display:      'flex',
              flexDirection:'column',
              gap:          3,
              border:       '1px solid var(--border)',
              borderRadius: 8,
              padding:      8,
            }}>
              {cidrs.map((c, i) => (
                <div
                  key={i}
                  style={{
                    display:     'flex',
                    alignItems:  'center',
                    gap:         8,
                    padding:     '3px 6px',
                    borderRadius: 5,
                    background:  !isValidCidr(c) ? 'rgba(255,107,107,.08)' : 'transparent',
                  }}
                >
                  <span style={{
                    flex:       1,
                    fontFamily: 'monospace',
                    fontSize:   12,
                    color:      !isValidCidr(c) ? '#ff6b6b' : undefined,
                  }}>
                    {c}
                  </span>
                  <button
                    className="icon-btn"
                    type="button"
                    onClick={() => onChange(cidrs.filter((_, j) => j !== i))}
                    title="Remove"
                    style={{ padding: 2, flexShrink: 0 }}
                  >
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Policy editor panel ──────────────────────────────────────────────────────
function PolicyEditor({
  policy,
  onSave,
  onDelete,
}: {
  policy:   NetworkPolicy;
  onSave:   (updated: NetworkPolicy) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [draft,  setDraft]  = useState<NetworkPolicy>({ ...policy, cidrs: [...policy.cidrs] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft({ ...policy, cidrs: [...policy.cidrs] });
  }, [policy.id]);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(draft);
      toast.success(`"${draft.name}" saved.`);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete policy "${policy.name}"? This cannot be undone.`)) return;
    try {
      await onDelete(policy.id);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Name */}
      <div>
        <label className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 5 }}>Policy Name</label>
        <input
          className="input"
          type="text"
          value={draft.name}
          onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
          style={{ width: '100%', fontSize: 14, fontWeight: 600 }}
        />
      </div>

      {/* Description */}
      <div>
        <label className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 5 }}>Description</label>
        <input
          className="input"
          type="text"
          value={draft.description || ''}
          onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
          style={{ width: '100%', fontSize: 13 }}
        />
      </div>

      {/* Enabled */}
      <Toggle
        checked={draft.enabled}
        onChange={v => setDraft(d => ({ ...d, enabled: v }))}
        label="Policy enabled"
        sublabel="Disabled policies are skipped during IP evaluation"
      />

      {/* CIDRs */}
      <div>
        <label className="muted" style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', display: 'block', marginBottom: 8 }}>
          Whitelisted IPs / CIDR Ranges
        </label>
        <CidrEditor
          cidrs={draft.cidrs}
          onChange={cidrs => setDraft(d => ({ ...d, cidrs }))}
        />
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, paddingTop: 4 }}>
        <button
          className="btn primary"
          type="button"
          disabled={saving}
          onClick={handleSave}
          style={{ gap: 6, fontSize: 13 }}
        >
          <Save size={14} />
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        {policy.id !== 'zscaler-hub' && (
          <button
            className="btn"
            type="button"
            onClick={handleDelete}
            style={{ gap: 6, fontSize: 13, color: 'var(--bad)', borderColor: 'rgba(255,107,107,.3)', marginLeft: 'auto' }}
          >
            <Trash2 size={13} /> Delete Policy
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function NetworkPolicies() {
  const [config,     setConfig]     = useState<NetworkConfig | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating,   setCreating]   = useState(false);
  const [newName,    setNewName]    = useState('');
  const [savingGlobal, setSavingGlobal] = useState(false);
  const newNameRef = useRef<HTMLInputElement>(null);

  // IP status for the current user (reuse from context)
  const ipCtx = useNetworkPolicy();

  useEffect(() => {
    apiFetch('/api/network-policies')
      .then((data: NetworkConfig) => {
        setConfig(data);
        if (data.policies.length > 0) setSelectedId(data.policies[0].id);
        setLoading(false);
      })
      .catch(e => {
        toast.error('Failed to load network policies: ' + e.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (creating) setTimeout(() => newNameRef.current?.focus(), 50);
  }, [creating]);

  const selected = config?.policies.find(p => p.id === selectedId) ?? null;

  async function handleGlobalToggle(field: 'enabled' | 'exemptAdmins', value: boolean) {
    if (!config) return;
    setSavingGlobal(true);
    try {
      const updated = await apiFetch('/api/network-policies', {
        method:  'PUT',
        headers: { 'content-type': 'application/json' },
        body:    JSON.stringify({ [field]: value }),
      }) as NetworkConfig;
      setConfig(updated);
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingGlobal(false);
    }
  }

  async function handleSavePolicy(updated: NetworkPolicy) {
    const result = await apiFetch(`/api/network-policy?id=${updated.id}`, {
      method:  'PUT',
      headers: { 'content-type': 'application/json' },
      body:    JSON.stringify(updated),
    }) as NetworkPolicy;
    setConfig(c => c ? { ...c, policies: c.policies.map(p => p.id === result.id ? result : p) } : c);
  }

  async function handleDeletePolicy(id: string) {
    await apiFetch(`/api/network-policy?id=${id}`, { method: 'DELETE' });
    setConfig(c => {
      if (!c) return c;
      const remaining = c.policies.filter(p => p.id !== id);
      return { ...c, policies: remaining };
    });
    setSelectedId(prev => {
      if (prev !== id) return prev;
      return config?.policies.find(p => p.id !== id)?.id ?? null;
    });
    toast.success('Policy deleted.');
  }

  async function handleCreate() {
    const name = newName.trim();
    if (!name) return;
    const result = await apiFetch('/api/network-policies', {
      method:  'POST',
      headers: { 'content-type': 'application/json' },
      body:    JSON.stringify({ name, cidrs: [], enabled: true }),
    }) as NetworkPolicy;
    setConfig(c => c ? { ...c, policies: [...c.policies, result] } : c);
    setSelectedId(result.id);
    setCreating(false);
    setNewName('');
    toast.success(`Policy "${name}" created.`);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div>
        <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.01em' }}>Network Policies</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
          Control which IP addresses and CIDR ranges are allowed to access this application
        </div>
      </div>

      {loading ? (
        <div className="muted" style={{ fontSize: 13 }}>Loading…</div>
      ) : !config ? null : (
        <>
          {/* Global settings */}
          <div className="card" style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>Global Settings</div>

            <Toggle
              checked={config.enabled}
              onChange={v => handleGlobalToggle('enabled', v)}
              label="Enable network policy enforcement"
              sublabel={
                config.enabled
                  ? 'Enforcement is ON — users not in any whitelisted range will be blocked'
                  : 'Enforcement is OFF — all authenticated users are allowed regardless of IP'
              }
            />

            <Toggle
              checked={config.exemptAdmins}
              onChange={v => handleGlobalToggle('exemptAdmins', v)}
              label="Exempt admin users"
              sublabel="Admins bypass the IP check — recommended to prevent accidental lockout"
            />

            {savingGlobal && <div className="muted" style={{ fontSize: 11 }}>Saving…</div>}

            {config.enabled && (
              <div style={{
                display:      'flex',
                alignItems:   'center',
                gap:          8,
                padding:      '10px 14px',
                borderRadius: 8,
                background:   'rgba(255,193,7,.07)',
                border:       '1px solid rgba(255,193,7,.25)',
                fontSize:     12,
                color:        '#ffc107',
              }}>
                <AlertTriangle size={14} style={{ flexShrink: 0 }} />
                Policy is active. Ensure your IPs or Zscaler ranges are whitelisted before logging off.
              </div>
            )}
          </div>

          {/* Your IP status */}
          {ipCtx.ready && (
            <div className="card" style={{ padding: '14px 18px' }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                Your Current Session
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {ipCtx.allowed
                  ? <CheckCircle2 size={15} style={{ color: '#51cf66', flexShrink: 0 }} />
                  : <AlertTriangle size={15} style={{ color: '#ff6b6b', flexShrink: 0 }} />
                }
                <span style={{ fontSize: 13, color: ipCtx.allowed ? '#51cf66' : '#ff6b6b', fontWeight: 500 }}>
                  {ipCtx.allowed ? 'Allowed' : 'Would be blocked'}
                </span>
                <span className="muted" style={{ fontSize: 12 }}>— {ipCtx.reason}</span>
              </div>
              {ipCtx.detectedIps.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {ipCtx.detectedIps.map(ip => (
                    <span key={ip} style={{
                      fontFamily:   'monospace',
                      fontSize:     11,
                      padding:      '2px 8px',
                      borderRadius: 4,
                      background:   'rgba(255,255,255,.06)',
                      border:       '1px solid var(--border)',
                    }}>
                      {ip}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Policy list + editor */}
          <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 16, alignItems: 'start' }}>

            {/* Left: policy list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {config.policies.map(p => (
                <div
                  key={p.id}
                  className="card"
                  onClick={() => setSelectedId(p.id)}
                  style={{
                    padding:    '12px 14px',
                    cursor:     'pointer',
                    borderLeft: `3px solid ${selectedId === p.id ? ACCENT : 'transparent'}`,
                    background: selectedId === p.id ? `${ACCENT}10` : undefined,
                    transition: 'background .1s, border-color .1s',
                    opacity:    p.enabled ? 1 : 0.55,
                  }}
                  onMouseEnter={e => { if (selectedId !== p.id) e.currentTarget.style.background = 'rgba(255,255,255,.03)'; }}
                  onMouseLeave={e => { if (selectedId !== p.id) e.currentTarget.style.background = ''; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <Shield size={13} style={{ color: selectedId === p.id ? ACCENT : 'var(--muted)', flexShrink: 0 }} />
                    <span style={{ fontSize: 13, fontWeight: selectedId === p.id ? 600 : 400, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </span>
                    {selectedId === p.id && <ChevronRight size={13} style={{ color: ACCENT, flexShrink: 0 }} />}
                  </div>
                  <div className="muted" style={{ fontSize: 10, marginTop: 3, marginLeft: 20 }}>
                    {p.cidrs.length} CIDR{p.cidrs.length !== 1 ? 's' : ''} · {p.enabled ? 'enabled' : 'disabled'}
                  </div>
                </div>
              ))}

              {/* New policy */}
              {creating ? (
                <div className="card" style={{ padding: '10px 12px', display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    ref={newNameRef}
                    className="input"
                    type="text"
                    placeholder="Policy name"
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleCreate();
                      if (e.key === 'Escape') { setCreating(false); setNewName(''); }
                    }}
                    style={{ flex: 1, fontSize: 12 }}
                  />
                  <button className="btn primary" type="button" onClick={handleCreate} style={{ fontSize: 11, padding: '4px 8px' }}>Add</button>
                  <button className="icon-btn" type="button" onClick={() => { setCreating(false); setNewName(''); }}><X size={13} /></button>
                </div>
              ) : (
                <button
                  className="btn"
                  type="button"
                  onClick={() => setCreating(true)}
                  style={{ gap: 6, fontSize: 12, justifyContent: 'center' }}
                >
                  <Plus size={13} /> New Policy
                </button>
              )}
            </div>

            {/* Right: editor */}
            <div className="card" style={{ padding: '20px 22px' }}>
              {selected ? (
                <PolicyEditor
                  key={selected.id}
                  policy={selected}
                  onSave={handleSavePolicy}
                  onDelete={handleDeletePolicy}
                />
              ) : (
                <div className="muted" style={{ fontSize: 13 }}>Select a policy to edit.</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
